import { useCallback, useEffect, useRef, useState } from 'react';
import { getToken, isSupported, onMessage } from 'firebase/messaging';
import axios from 'axios';
import toast from 'react-hot-toast';
import { registerPushServiceWorker } from '../utils/pushServiceWorker';

// typeof is the only safe way to probe an identifier that may not be declared at all.
// Notification?.permission would still throw ReferenceError on iOS Safari because
// the variable itself doesn't exist -- optional chaining only handles null/undefined.
function getNotificationPermission() {
  try {
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  } catch {
    return 'default';
  }
}

function notificationsSupported() {
  return (
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const { REACT_APP_BASE_URL } = process.env;

async function syncFcmTokenToBackend(fcmToken) {
  const authToken = localStorage.getItem('token');
  const canvasser = localStorage.getItem('urserMail');
  if (!authToken || !canvasser || !fcmToken) return;
  try {
    await axios.post(
      `${REACT_APP_BASE_URL}/update_fcm_token`,
      { fcmToken, canvasser },
      { headers: { 'x-access-token': authToken, 'Content-Type': 'application/json' } }
    );
  } catch (err) {
    console.warn('FCM token sync failed:', err?.response?.data || err.message);
  }
}

// messagingReady is a Promise<FirebaseMessaging | null> exported from firebase.js.
// We accept the promise so the caller never has to await before rendering.
function useFirebaseMessaging(messagingReady) {
  const [messaging, setMessaging] = useState(null);
  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState(getNotificationPermission);
  const unsubscribeRef = useRef(null);

  // Resolve the messaging promise once on mount.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(messagingReady)
      .then((m) => { if (!cancelled) setMessaging(m || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [messagingReady]);

  // When permission is (or becomes) granted, set up the foreground message listener
  // and refresh the FCM token. Runs for both "already granted on page load" and
  // "just granted via requestPermission()".
  useEffect(() => {
    if (!messaging || !notificationsSupported() || permission !== 'granted') {
      return;
    }

    let cancelled = false;

    const refreshToken = async () => {
      try {
        if (!VAPID_KEY) {
          console.error('FCM: REACT_APP_FIREBASE_VAPID_KEY is not set. Token fetch aborted.');
          return;
        }

        // Hand sw.js's registration to getToken() so Firebase never auto-registers a second worker at root scope.
        let swReg;
        try {
          swReg = await registerPushServiceWorker();
        } catch (swErr) {
          console.warn('FCM: service worker registration failed:', swErr.message);
          return;
        }

        const newToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg,
        });

        if (cancelled) return;

        if (!newToken) {
          // Empty token with no error means the browser created the service
          // worker but could not complete the push subscription. On iOS this
          // usually means the user allowed the in-app prompt but the system
          // permission in Settings > Notifications is still set to off.
          console.warn(
            'FCM: getToken() returned empty. On iPad/iPhone go to ' +
            'Settings > Notifications > [App] and make sure Allow Notifications is on.'
          );
          return;
        }

        const stored = localStorage.getItem('fcmToken');
        if (newToken !== stored) {
          localStorage.setItem('fcmToken', newToken);
          setToken(newToken);
          await syncFcmTokenToBackend(newToken);
        } else {
          setToken(newToken);
        }
      } catch (e) {
        console.warn('FCM token refresh failed:', e.code ?? e.message);
      }
    };

    refreshToken();

    // Re-check the token once every 24 hours in case Firebase rotated the
    // push subscription. syncFcmTokenToBackend only posts if the token changed.
    const refreshInterval = setInterval(refreshToken, 24 * 60 * 60 * 1000);

    unsubscribeRef.current = onMessage(messaging, (payload) => {
      console.log('[FCM] Foreground message received:', payload);
      const title = payload.data?.title || payload.notification?.title;
      const body = payload.data?.body || payload.notification?.body || '';
      console.log('[FCM] Foreground - title:', title, '| body:', body);
      if (!title) return;

      // In-app toast when the app is open in the foreground.
      toast(
        (t) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }} onClick={() => toast.dismiss(t.id)}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
            {body && <span style={{ fontSize: 12, color: '#555' }}>{body}</span>}
          </div>
        ),
        { duration: 6000, icon: '🔔', style: { maxWidth: 360 } }
      );

      // OS / service-worker notification (background-style, existing behaviour).
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, {
            body,
            icon: '/android-chrome-192x192.png',
            badge: '/android-chrome-192x192.png',
            requireInteraction: true,
            tag: 'canvassing-reminder',
          });
        });
      }
    });

    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [messaging, permission]);

  // Call this from a button click handler -- never auto-call on mount.
  const requestPermission = useCallback(async () => {
    if (!messaging || !notificationsSupported()) return;
    try {
      // isSupported() is Firebase's authoritative async check. Calling it here
      // prevents the internal unsupported-browser promise rejection on iOS Safari.
      const supported = await isSupported();
      if (!supported) return;
      const result = await Notification.requestPermission();
      setPermission(result);
      // If granted, the useEffect above fires automatically.
    } catch (e) {
      console.warn('Push notification permission request failed:', e.message);
    }
  }, [messaging]);

  return { token, permission, requestPermission };
}

export default useFirebaseMessaging;
